import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ScrollReveal from '@/components/base/ScrollReveal';

const makiItemsJa = [
  'のり巻', 'かっぱ巻', 'おしんこ巻', 'ごぼう巻', '納豆巻',
  '梅しそ巻', '鉄火巻', 'とろ巻', 'ねぎとろ巻', 'とろたく巻',
  '太巻', 'のり玉', 'お子様寿司',
];

const makiItemsEn = [
  'Nori Maki', 'Kappa Maki', 'Oshinko Maki', 'Gobo Maki', 'Natto Maki',
  'Ume Shiso Maki', 'Tekka Maki', 'Toro Maki', 'Negi Toro Maki', 'Toro Taku Maki',
  'Futo Maki', 'Nori Tama', 'Kids Sushi',
];

const headerBgImage = 'https://storage.helloreaddy.io/project_files/fbab44cd-04c6-492b-a442-ed8f359ae362/aa133f5c-b70f-41c6-abe0-1cd52e276c5b_compressed_-2026-09-07-16.55.11.webp';

interface MenuContentProps {
  topPadding?: boolean;
}

export default function MenuContent({ topPadding = true }: MenuContentProps) {
  const { t, i18n } = useTranslation();
  const isJa = i18n.language === 'ja';
  const makiItems = isJa ? makiItemsJa : makiItemsEn;

  const MenuRow = ({ name, price }: { name: string; price: string }) => (
    <div className="flex items-center justify-between py-5 px-4 border-b border-secondary-200/40 group hover:bg-secondary-50/40 transition-all duration-1000 cursor-pointer">
      <span className="font-heading text-xl md:text-2xl font-bold tracking-wider text-foreground-900 group-hover:text-accent-600 group-hover:-translate-y-1 transition-all duration-1000">
        {name}
      </span>
      <span className="font-heading text-xl md:text-2xl font-bold text-foreground-800 group-hover:-translate-y-1 transition-all duration-1000">
        {price}
      </span>
    </div>
  );

  return (
    <>
      {/* Page Header */}
      <section className={`relative ${topPadding ? 'pt-28 md:pt-36' : 'pt-16 md:pt-20'} pb-12 md:pb-16 px-4 md:px-6 overflow-hidden`}>
        <img src={headerBgImage} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <p className="font-heading text-xs md:text-sm tracking-[0.3em] text-accent-300 mb-4">
            {t('menu_label')}
          </p>
          <h2 className="font-heading text-2xl md:text-3xl tracking-wider text-white mb-4">
            {t('menu_title')}
          </h2>
        </div>
      </section>

      {/* Menu Items */}
      <section className="py-16 md:py-20 px-4 md:px-6">
        <div className="max-w-6xl mx-auto space-y-16 md:space-y-20">
          {/* LUNCH */}
          <ScrollReveal>
            <div className="border border-secondary-300/60 rounded-lg bg-white/50 p-6 md:p-10">
              <div className="text-center mb-12">
                <h3 className="font-heading text-2xl md:text-3xl font-bold tracking-wider text-foreground-900 mb-2">
                  {t('lunch_title')}
                </h3>
                <p className="text-sm tracking-widest text-accent-600 font-label">{t('lunch_time')}</p>
              </div>
              <div className="max-w-2xl mx-auto">
                <MenuRow name={t('lunch_barachirashi')} price={`${t('lunch_barachirashi_price')}円`} />
                <MenuRow name={t('lunch_tekkadon')} price={`${t('lunch_tekkadon_price')}円`} />
              </div>
            </div>
          </ScrollReveal>

          {/* COURSE */}
          <ScrollReveal>
            <div className="border border-secondary-300/60 rounded-lg bg-white/50 p-6 md:p-10">
              <div className="text-center mb-12">
                <h3 className="font-heading text-2xl md:text-3xl font-bold tracking-wider text-foreground-900 mb-2">
                  {t('course_title')}
                </h3>
                <p className="text-sm tracking-widest text-accent-600 font-label max-w-xl mx-auto whitespace-pre-line">
                  {t('course_subtitle')}
                </p>
              </div>
              <div className="max-w-3xl mx-auto">
                <MenuRow name={t('course_um')} price={`${t('course_um_price')}${t('yen')}`} />
                <MenuRow name={t('course_take')} price={`${t('course_take_price')}${t('yen')}`} />
                <MenuRow name={t('course_matsu')} price={`${t('course_matsu_price')}${t('yen')}`} />
              </div>
            </div>
          </ScrollReveal>

          {/* NIGIRI */}
          <ScrollReveal>
            <div className="border border-secondary-300/60 rounded-lg bg-white/50 p-6 md:p-10">
              <div className="text-center mb-12">
                <h3 className="font-heading text-2xl md:text-3xl font-bold tracking-wider text-foreground-900 mb-2">
                  {t('nigiri_title')}
                </h3>
                <p className="text-sm tracking-widest text-accent-600 font-label">{t('nigiri_subtitle')}</p>
              </div>
              <div className="max-w-3xl mx-auto">
                <MenuRow name={t('nigiri_nami')} price={`${t('nigiri_nami_price')}${t('yen')}`} />
                <MenuRow name={t('nigiri_jo')} price={`${t('nigiri_jo_price')}${t('yen')}`} />
                <MenuRow name={t('nigiri_tokujo')} price={`${t('nigiri_tokujo_price')}${t('yen')}`} />
                <MenuRow name={t('nigiri_omakase')} price={`${t('nigiri_omakase_price')}${t('yen')}`} />
              </div>
            </div>
          </ScrollReveal>

          {/* MAGURO */}
          <ScrollReveal>
            <div className="border border-secondary-300/60 rounded-lg bg-white/50 p-6 md:p-10">
              <div className="text-center mb-12">
                <h3 className="font-heading text-2xl md:text-3xl font-bold tracking-wider text-foreground-900 mb-2">
                  {t('maguro_title')}
                </h3>
                <p className="text-sm tracking-widest text-accent-600 font-label">{t('maguro_subtitle')}</p>
              </div>
              <div className="max-w-3xl mx-auto">
                <MenuRow name={t('maguro_tekkaju')} price={`${t('maguro_tekkaju_price')}${t('yen')}`} />
                <MenuRow name={t('maguro_chutoroju')} price={`${t('maguro_chutoroju_price')}${t('yen')}`} />
                <MenuRow name={t('maguro_akaminigiri')} price={`${t('maguro_akaminigiri_price')}${t('yen')}`} />
                <MenuRow name={t('maguro_chutoronigiri')} price={`${t('maguro_chutoronigiri_price')}${t('yen')}`} />
              </div>
            </div>
          </ScrollReveal>

          {/* SAKE */}
          <ScrollReveal>
            <div className="border border-secondary-300/60 rounded-lg bg-white/50 p-6 md:p-10">
              <div className="text-center mb-12">
                <h3 className="font-heading text-2xl md:text-3xl font-bold tracking-wider text-foreground-900 mb-2">
                  {t('sake_title')}
                </h3>
                <p className="text-sm tracking-widest text-accent-600 font-label">{t('sake_subtitle')}</p>
              </div>
              <p className="text-base md:text-lg text-foreground-700 leading-loose max-w-2xl mx-auto text-center whitespace-pre-line">
                {t('sake_desc')}
              </p>
            </div>
          </ScrollReveal>

          {/* MAKIMONO */}
          <ScrollReveal>
            <div className="border border-secondary-300/60 rounded-lg bg-white/50 p-6 md:p-10">
              <div className="text-center mb-12">
                <h3 className="font-heading text-2xl md:text-3xl font-bold tracking-wider text-foreground-900 mb-2">
                  {t('maki_title')}
                </h3>
                <p className="text-sm tracking-widest text-accent-600 font-label">{t('maki_subtitle')}</p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-4 max-w-4xl mx-auto">
                {makiItems.map((item) => (
                  <div
                    key={item}
                    className="py-3 px-2 text-center group hover:bg-secondary-50/40 transition-all duration-1000 cursor-pointer"
                  >
                    <span className="font-heading text-lg md:text-xl font-bold tracking-wider text-foreground-800 group-hover:text-accent-600 group-hover:-translate-y-1 transition-all duration-1000">
                      {item}
                    </span>
                  </div>
                ))}
              </div>
              <p className="text-center text-sm text-foreground-500 mt-10 tracking-wider">
                {t('other_items')}
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Reservation CTA */}
      <section className="py-16 px-4 md:px-6">
        <div className="max-w-2xl mx-auto text-center">
          <Link
            to="/reservation"
            className="inline-flex items-center gap-3 px-10 py-4 bg-primary-500 text-background-50 text-sm tracking-widest rounded-md hover:bg-primary-600 transition-all duration-500 font-label whitespace-nowrap"
          >
            <i className="ri-calendar-check-line text-lg" />
            {t('menu_reservation')}
          </Link>
        </div>
      </section>
    </>
  );
}